import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { AlertButton } from "./AlertButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#C9A961";
const REFRESH_MS = 60000;

export function Sparkline({ series, width = 120, height = 34, color = GOLD, testid }) {
  const pts = (series || []).map((p) => p.price);
  if (pts.length < 2) return <svg width={width} height={height} data-testid={testid} />;
  const min = Math.min(...pts), max = Math.max(...pts), span = max - min || 1;
  const step = width / (pts.length - 1);
  const coords = pts.map((v, i) => [i * step, height - 3 - ((v - min) / span) * (height - 6)]);
  const d = coords.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${d} L${width},${height} L0,${height} Z`;
  const [lx, ly] = coords[coords.length - 1];
  const id = `sg-${testid || Math.random().toString(36).slice(2)}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} data-testid={testid} className="overflow-visible">
      <defs><linearGradient id={id} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity="0.35" /><stop offset="100%" stopColor={color} stopOpacity="0" /></linearGradient></defs>
      <path d={area} fill={`url(#${id})`} />
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" className="gold-spark-path" />
      <circle cx={lx} cy={ly} r="2.2" fill={color} style={{ filter: `drop-shadow(0 0 4px ${color})` }} />
    </svg>
  );
}

function usePulseOnChange(value) {
  const prev = useRef(value);
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    if (prev.current !== undefined && value !== prev.current) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 2200);
      prev.current = value;
      return () => clearTimeout(t);
    }
    prev.current = value;
  }, [value]);
  return pulse;
}

function MetalCard({ r, series, alerts, onAlertCreated }) {
  const pulse = usePulseOnChange(r.price);
  const up = r.trend === "up", down = r.trend === "down";
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  const armed = (alerts || []).filter((a) => a.status === "active").length;
  return (
    <div data-testid={`metal-card-${r.category}`} data-pulse={pulse} className={`relative rounded-sm border border-[#C9A961]/20 bg-[#0a0a0c]/85 backdrop-blur-sm p-4 ${pulse ? "gold-card-glow" : ""}`}>
      <div className="absolute inset-x-0 top-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`, opacity: 0.5 }} />
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-grotesk text-[9px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>{r.label}</div>
          <div data-testid={`metal-price-${r.category}`} className={`font-serif text-2xl leading-none mt-1 text-[#F5F5F0] ${pulse ? "gold-price-pulse" : ""}`}>₹{Number(r.price).toLocaleString("en-IN", { maximumFractionDigits: 2 })}<span className="font-grotesk text-[9px] text-[#8A8A82] ml-1">/kg</span></div>
        </div>
        <AlertButton compact category={r.category} label={r.label} unit={r.unit} price={r.price} color={GOLD} onCreated={onAlertCreated} />
      </div>
      <div className="mt-3 flex items-end justify-between gap-2">
        <Sparkline series={series} testid={`metal-spark-${r.category}`} />
        <div className="text-right">
          <div className="inline-flex items-center gap-1 font-grotesk text-[10px]" style={{ color: up ? "#2FB999" : down ? "#E07B72" : "#8A8A82" }} data-testid={`metal-change-${r.category}`}>
            <Icon size={11} />{r.change_pct > 0 ? "+" : ""}{Number(r.change_pct).toFixed(2)}%
          </div>
          <div className="font-grotesk text-[8px] uppercase tracking-[0.18em] text-[#6f6f68] mt-1">{armed ? `${armed} target${armed > 1 ? "s" : ""} armed` : "30D"}</div>
        </div>
      </div>
    </div>
  );
}

export const MetalIndexStrip = ({ alertsByCategory = {}, onAlertCreated }) => {
  const [idx, setIdx] = useState(null);
  const [hist, setHist] = useState({});
  useEffect(() => {
    let alive = true;
    const load = () => axios.get(`${API}/rates/metal-index`).then((r) => alive && setIdx(r.data)).catch(() => {});
    load();
    axios.get(`${API}/rates/history?days=30`).then((r) => {
      if (!alive) return;
      const m = {};
      for (const row of r.data || []) if (row.group === "metal_index") m[row.category] = row.series;
      setHist(m);
    }).catch(() => {});
    const id = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);
  if (!idx?.rows?.length) return null;
  return (
    <section data-testid="metal-index-strip" className="mb-10">
      <div className="flex items-end justify-between gap-4 mb-4 flex-wrap">
        <div>
          <span className="font-grotesk text-[10px] uppercase tracking-[0.28em]" style={{ color: GOLD }}>Vinci Metal Index</span>
          <h2 className="font-serif text-xl md:text-2xl text-[#F5F5F0] mt-1">Non-ferrous & flat-steel benchmarks · 30-day direction</h2>
        </div>
        <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#6f6f68]" data-testid="metal-index-meta">{idx.reference} · set a target on any metal to be emailed when the desk crosses it</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {idx.rows.map((r) => <MetalCard key={r.category} r={r} series={hist[r.category]} alerts={alertsByCategory[r.category]} onAlertCreated={onAlertCreated} />)}
      </div>
    </section>
  );
};
